import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { login as loginApi } from '@/services/api/auth.service'
import { ApiError } from '@/services/api/client'
import { useAuthStore } from '@/stores/auth.store'



const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

interface LoginFormProps {
  defaultEmail?: string
  emailReadOnly?: boolean
}

export function LoginForm({ defaultEmail = '', emailReadOnly = false }: LoginFormProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setUserId, setLoginToken, setWorkspaces, setAuthStep, setEmail } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: defaultEmail, password: '' },
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      const response = await loginApi(data)
      setEmail(data.email)
      setUserId(response.userId)
      setLoginToken(response.loginToken)
      setWorkspaces(response.workspaces)
      setAuthStep('workspace')
      navigate('/workspace-select')
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.type.includes('authentication')) {
          setError('email', { message: t('errors.invalidCredentials') })
          setError('password', { message: t('errors.invalidCredentials') })
          toast.error('Invalid Credentials', { description: 'Email or password is incorrect' })
        } else {
          toast.error(error.title, { description: error.detail })
        }
      } else {
        toast.error(t('errors.networkError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">{t('auth.login')}</CardTitle>
        <CardDescription>
          Enter your credentials to access your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              {...register('email')}
              readOnly={emailReadOnly}
              className={emailReadOnly ? 'bg-neutral-100 cursor-not-allowed text-neutral-500' : ''}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="text-sm text-destructive-600">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <p className="text-sm text-destructive-600">{errors.password.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : t('auth.login')}
          </Button>
          <div className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:underline">Forgot password?</Link>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
